/**
 * Local IndexedDB-backed store. We use raw IndexedDB (via a thin wrapper)
 * instead of RxDB because the data we cache is already ciphertext from the
 * server — RxDB's encryption layer would double-encrypt without added value.
 *
 * Schemas store the EncryptedPayload (iv + ciphertext) verbatim, plus the
 * server's plaintext metadata (ids, timestamps, deleted flags). The caller
 * decrypts on read using the in-memory vault key.
 */

const DB_NAME = 'dragonvault';
const DB_VERSION = 1;

export interface Stored<T> {
  id: string;
  updatedAt: string;
  createdAt: string;
  deletedAt: string | null;
  pendingSync: boolean;
  data: T;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('customers')) {
        db.createObjectStore('customers', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('documents')) {
        const s = db.createObjectStore('documents', { keyPath: 'id' });
        s.createIndex('customerId', 'data.customerId', { unique: false });
      }
      if (!db.objectStoreNames.contains('outbox')) {
        db.createObjectStore('outbox', { keyPath: 'opId' });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function tx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | Promise<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const s = t.objectStore(store);
    const result = fn(s);
    if (result instanceof Promise) {
      result.then(resolve, reject);
      return;
    }
    result.onsuccess = () => resolve(result.result);
    result.onerror = () => reject(result.error);
  });
}

export const localStore = {
  async putCustomer(row: Stored<any>) {
    return tx('customers', 'readwrite', (s) => s.put(row));
  },
  async getCustomer(id: string) {
    return tx<Stored<any> | undefined>('customers', 'readonly', (s) => s.get(id) as any);
  },
  async listCustomers(): Promise<Stored<any>[]> {
    return tx<Stored<any>[]>('customers', 'readonly', (s) => s.getAll() as any);
  },
  async deleteCustomer(id: string) {
    return tx('customers', 'readwrite', (s) => s.delete(id));
  },

  async putDocument(row: Stored<any>) {
    return tx('documents', 'readwrite', (s) => s.put(row));
  },
  async getDocument(id: string) {
    return tx<Stored<any> | undefined>('documents', 'readonly', (s) => s.get(id) as any);
  },
  async listDocuments(): Promise<Stored<any>[]> {
    return tx<Stored<any>[]>('documents', 'readonly', (s) => s.getAll() as any);
  },
  async deleteDocument(id: string) {
    return tx('documents', 'readwrite', (s) => s.delete(id));
  },
  async listDocumentsByCustomer(customerId: string): Promise<Stored<any>[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const t = db.transaction('documents', 'readonly');
      const s = t.objectStore('documents');
      const idx = s.index('customerId');
      const req = idx.getAll(IDBKeyRange.only(customerId));
      req.onsuccess = () => resolve(req.result as Stored<any>[]);
      req.onerror = () => reject(req.error);
    });
  },

  async enqueueOp(op: OutboxOp) {
    return tx('outbox', 'readwrite', (s) => s.put({ ...op, opId: op.opId ?? `${Date.now()}-${Math.random()}` }));
  },
  async listOps(): Promise<OutboxOp[]> {
    return tx<OutboxOp[]>('outbox', 'readonly', (s) => s.getAll() as any);
  },
  async updateOp(op: OutboxOp) {
    // Re-put a queued op carrying refreshed attemptCount/lastError. opId is
    // required — an op without one was never enqueued.
    if (!op.opId) throw new Error('updateOp requires opId');
    return tx('outbox', 'readwrite', (s) => s.put(op));
  },
  async removeOp(opId: string) {
    return tx('outbox', 'readwrite', (s) => s.delete(opId));
  },

  async getMeta(key: string): Promise<string | null> {
    const row = await tx<{ key: string; value: string } | undefined>('meta', 'readonly', (s) => s.get(key) as any);
    return row?.value ?? null;
  },
  async setMeta(key: string, value: string) {
    return tx('meta', 'readwrite', (s) => s.put({ key, value }));
  },

  /**
   * Permanently-failed sync events. An outbox op or snapshot PUT that the
   * server definitively rejected (403 / 410 / 404 / session_revoked) is
   * dropped from the active queues — but we record a breadcrumb here so
   * the user has a sticky surface to acknowledge / review the loss
   * instead of relying on an auto-dismissing toast (see plan F4).
   *
   * Stored in the existing `meta` store under FAILED_EVENTS_KEY as a
   * JSON array — keeps the schema migration-free. The count is small
   * by construction (genuine permanent failures are rare).
   */
  async listFailedEvents(): Promise<FailedSyncEvent[]> {
    const raw = await this.getMeta(FAILED_EVENTS_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  },
  async appendFailedEvent(event: FailedSyncEvent) {
    const current = await this.listFailedEvents();
    current.push(event);
    // Cap to a sensible ceiling so a stuck-loop bug can't blow out the row.
    const capped = current.length > 200 ? current.slice(-200) : current;
    await this.setMeta(FAILED_EVENTS_KEY, JSON.stringify(capped));
    notifyFailedEvents();
  },
  async clearFailedEvents() {
    await this.setMeta(FAILED_EVENTS_KEY, JSON.stringify([]));
    notifyFailedEvents();
  },

  async wipe() {
    const db = await openDb();
    for (const name of ['customers', 'documents', 'outbox', 'meta'] as const) {
      await new Promise<void>((resolve, reject) => {
        const t = db.transaction(name, 'readwrite');
        const s = t.objectStore(name);
        const req = s.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    }
  },
};

/**
 * One permanently-dropped sync event (outbox op or snapshot PUT). Persisted
 * via `localStore.appendFailedEvent` so the status light stays red until
 * the user acknowledges them via the Recover-failed-edits modal.
 */
export interface FailedSyncEvent {
  /** Unique id for de-dup + UI keys. */
  eventId: string;
  /** Whether this came from the outbox drain or a snapshot PUT. */
  source: 'outbox' | 'snapshot';
  /** Outbox op type when source==='outbox'. */
  opType?:
    | 'upsert_customer'
    | 'upsert_document'
    | 'delete_customer'
    | 'delete_document';
  /** True if this was a create (only meaningful for upsert ops). */
  isNew?: boolean;
  /** Document id when known (snapshot PUT or document outbox op). */
  documentId?: string;
  /** Customer id when known (customer outbox op or doc's parent). */
  customerId?: string;
  /** HTTP status; 0 if non-HTTP error (e.g. abort). */
  status: number;
  /** Server-provided error or our best-effort string. */
  message: string;
  /** ISO timestamp when the drop happened. */
  at: string;
}

/** Single meta key for the failed-events JSON array. */
const FAILED_EVENTS_KEY = 'vault.failedSyncEvents';

const failedEventListeners = new Set<() => void>();
export function subscribeFailedEvents(cb: () => void): () => void {
  failedEventListeners.add(cb);
  return () => failedEventListeners.delete(cb);
}
function notifyFailedEvents() {
  for (const cb of failedEventListeners) {
    try {
      cb();
    } catch (err) {
      console.warn('[vault] failed-events listener threw', err);
    }
  }
}

/**
 * Retry metadata carried on every queued op. Populated by `flushOutbox` on
 * each non-permanent failure so the user can see "5 unsynced edits, last
 * attempt failed: 503" via `useSyncStatus`. Permanent failures (404/401/403)
 * remove the op outright and surface a notification instead.
 */
export interface OutboxRetryMeta {
  attemptCount?: number;
  lastError?: string;
  lastAttemptAt?: string;
}

export type OutboxOp =
  | ({
      opId?: string;
      type: 'upsert_customer';
      id: string;
      payload: { iv: string; ciphertext: string; version: number };
      isNew: boolean;
    } & OutboxRetryMeta)
  | ({
      opId?: string;
      type: 'delete_customer';
      id: string;
    } & OutboxRetryMeta)
  | ({
      opId?: string;
      type: 'upsert_document';
      id: string;
      customerId: string;
      parentId: string | null;
      payload: { iv: string; ciphertext: string; version: number };
      isNew: boolean;
    } & OutboxRetryMeta)
  | ({
      opId?: string;
      type: 'delete_document';
      id: string;
    } & OutboxRetryMeta);
